(() => {
  'use strict';

  const CONFIG = Object.freeze({
    minViewedProducts: 3,
    minDisplayProducts: 4,
    maxDisplayProducts: 8,
    autoDelayMs: 4000,
    dismissDays: 5,
    autoShownSessionKey: 'sct_rec_bottom_auto_v1',
    surfaceSessionKey: 'sct_rec_surface_seen_v1',
    dismissedAtKey: 'sct_rec_bottom_dismissed_at_v1',
  });

  const query =
    new URLSearchParams(
      window.location.search
    );

  const debug =
    query.get('sct_debug') === '1';

  let products = [];
  let loadingPromise = null;
  let footerLocalizationVisible = false;
  let footerLocalizationObserver = null;
  let triggerPositionFrame = null;

  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number)
      ? number
      : 0;
  }

  function readContext() {
    const node =
      document.getElementById(
        'SctRecommendationContext'
      );

    if (!node) return {};

    try {
      return JSON.parse(
        node.textContent || '{}'
      );
    } catch (_error) {
      return {};
    }
  }

  function profile() {
    return (
      window.SctRecommendation
        ?.getProfile?.() ||
      null
    );
  }

  function eligible() {
    return (
      toNumber(
        profile()
          ?.uniqueViewedProducts
      ) >=
      CONFIG.minViewedProducts
    );
  }

  function readLocalNumber(key) {
    try {
      return Number(
        localStorage.getItem(key)
      ) || 0;
    } catch (_error) {
      return 0;
    }
  }

  function writeLocalNumber(
    key,
    value
  ) {
    try {
      localStorage.setItem(
        key,
        String(value)
      );
    } catch (_error) {
      // no-op
    }
  }

  function autoShownThisSession() {
    try {
      return (
        sessionStorage.getItem(
          CONFIG.autoShownSessionKey
        ) === '1'
      );
    } catch (_error) {
      return false;
    }
  }

  function markAutoShown() {
    try {
      sessionStorage.setItem(
        CONFIG.autoShownSessionKey,
        '1'
      );
    } catch (_error) {
      // no-op
    }
  }

  function surfaceSeenThisSession() {
    try {
      return (
        sessionStorage.getItem(
          CONFIG.surfaceSessionKey
        ) === '1'
      );
    } catch (_error) {
      return false;
    }
  }

  function markSurfaceSeen() {
    try {
      sessionStorage.setItem(
        CONFIG.surfaceSessionKey,
        '1'
      );
    } catch (_error) {
      // no-op
    }
  }

  function autoDismissed() {
    const dismissedAt =
      readLocalNumber(
        CONFIG.dismissedAtKey
      );

    if (!dismissedAt) {
      return false;
    }

    const dismissMs =
      CONFIG.dismissDays *
      24 *
      60 *
      60 *
      1000;

    return (
      Date.now() - dismissedAt <
      dismissMs
    );
  }

  function markDismissed() {
    writeLocalNumber(
      CONFIG.dismissedAtKey,
      Date.now()
    );
  }

  function getSheet() {
    return document.getElementById(
      'SctRecommendationBottomSheet'
    );
  }

  function getTriggerWrap() {
    return document.getElementById(
      'SctRecommendationBottomTrigger'
    );
  }

  function getTriggerButton() {
    return document.querySelector(
      '[data-sct-rec-bottom-open]'
    );
  }

  function getTrack() {
    return document.querySelector(
      '[data-sct-rec-bottom-track]'
    );
  }

  function formatPrice(priceCents) {
    const cents =
      toNumber(priceCents);

    if (cents <= 0) return '';

    const context =
      readContext();

    const currency =
      context.currency || 'JPY';

    const language =
      context.language ||
      document.documentElement.lang ||
      'en';

    const noDecimals =
      currency === 'JPY' ||
      currency === 'KRW';

    try {
      return new Intl.NumberFormat(
        language,
        {
          style: 'currency',
          currency,
          minimumFractionDigits:
            noDecimals ? 0 : 2,
          maximumFractionDigits:
            noDecimals ? 0 : 2,
        }
      ).format(
        cents / 100
      );
    } catch (_error) {
      return String(
        Math.round(cents / 100)
      );
    }
  }

  function textElement(
    tagName,
    className,
    text
  ) {
    const node =
      document.createElement(
        tagName
      );

    node.className =
      className;

    node.textContent =
      text || '';

    return node;
  }

  function card(
    product,
    position
  ) {
    const article =
      document.createElement(
        'article'
      );

    article.className =
      'sct-rec-bottom-card';

    article.setAttribute(
      'role',
      'listitem'
    );

    const link =
      document.createElement(
        'a'
      );

    link.className =
      'sct-rec-bottom-card__link';

    link.href =
      product.url ||
      `/products/${product.handle}`;

    link.dataset.sctAction =
      'recommendation_product_click';

    link.dataset.sctItemType =
      'product';

    link.dataset.sctItemId =
      String(product.id || '');

    link.dataset.sctItemHandle =
      product.handle || '';

    link.dataset.sctItemName =
      product.title || '';

    link.dataset.sctItemPosition =
      String(position);

    link.dataset.sctProductPosition =
      String(position);

    link.dataset.sctBrand =
      product.vendor || '';

    link.dataset.sctTabName =
      'bottom_sheet';

    link.dataset.sctDestinationType =
      'product';

    link.addEventListener(
      'click',
      () => {
        window.SctRecommendation
          ?.track?.(
            'click',
            {
              id: product.id,
              handle:
                product.handle,
              title:
                product.title,
              vendor:
                product.vendor,
              category:
                product.category,
              price_cents:
                product.price_cents,
              available:
                product.available,
              created_at:
                product.created_at,
              styles:
                product.styles,
            },
              {
                recommendation_source:
                  'bottom_sheet',
              }
            );
      },
      { passive: true }
    );

    const media =
      document.createElement(
        'div'
      );

    media.className =
      'sct-rec-bottom-card__media';

    if (product.image) {
      const image =
        document.createElement(
          'img'
        );

      image.className =
        'sct-rec-bottom-card__image';

      image.src =
        product.image;

      image.alt =
        product.title || '';

      image.loading =
        'lazy';

      image.decoding =
        'async';

      media.appendChild(image);
    } else {
      const placeholder =
        document.createElement(
          'div'
        );

      placeholder.className =
        'sct-rec-bottom-card__placeholder';

      media.appendChild(
        placeholder
      );
    }

    const info =
      document.createElement(
        'div'
      );

    info.className =
      'sct-rec-bottom-card__info';

    if (product.vendor) {
      info.appendChild(
        textElement(
          'p',
          'sct-rec-bottom-card__vendor',
          product.vendor
        )
      );
    }

    info.appendChild(
      textElement(
        'p',
        'sct-rec-bottom-card__title',
        product.title
      )
    );

    const price =
      formatPrice(
        product.price_cents
      );

    if (price) {
      info.appendChild(
        textElement(
          'p',
          'sct-rec-bottom-card__price',
          price
        )
      );
    }

    link.appendChild(media);
    link.appendChild(info);
    article.appendChild(link);

    return article;
  }

  function renderProducts() {
    const track = getTrack();

    if (!track) {
      return false;
    }

    track.textContent = '';

    products
      .slice(
        0,
        CONFIG.maxDisplayProducts
      )
      .forEach(
        (product, index) => {
          track.appendChild(
            card(
              product,
              index + 1
            )
          );
        }
      );

    return true;
  }

  async function getProducts() {
    if (
      products.length >=
      CONFIG.minDisplayProducts
    ) {
      return products;
    }

    if (loadingPromise) {
      return loadingPromise;
    }

    const engine =
      window
        .SctRecommendationEngine;

    if (!engine?.recommend) {
      return [];
    }

    const last =
      engine.getLastResult?.();

    if (
      Array.isArray(last?.products) &&
      last.products.length >=
        CONFIG.minDisplayProducts
    ) {
      products =
        last.products.slice(
          0,
          CONFIG.maxDisplayProducts
        );

      return products;
    }

    loadingPromise =
      engine.recommend()
        .then(result => {
          const resultProducts =
            Array.isArray(
              result?.products
            )
              ? result.products
              : [];

          products =
            resultProducts.slice(
              0,
              CONFIG.maxDisplayProducts
            );

          return products;
        })
        .catch(error => {
          if (debug) {
            console.warn(
              '[SCT Recommendation Bottom Sheet] Candidate load failed.',
              error
            );
          }

          return [];
        })
        .finally(() => {
          loadingPromise = null;
        });

    return loadingPromise;
  }

  function sheetIsOpen() {
    const sheet = getSheet();

    return Boolean(
      sheet &&
      !sheet.hidden &&
      sheet.classList.contains(
        'is-open'
      )
    );
  }

  function updateTriggerPositionForFooter() {
    const triggerWrap =
      getTriggerWrap();

    const localization =
      document.querySelector(
        '.footer__localization'
      );

    if (!triggerWrap) {
      return;
    }

    if (
      window.innerWidth > 749 ||
      !localization
    ) {
      footerLocalizationVisible = false;

      triggerWrap.style.removeProperty(
        '--sct-rec-trigger-bottom'
      );

      return;
    }

    const rect =
      localization
        .getBoundingClientRect();

    const viewportHeight =
      window.innerHeight ||
      document.documentElement
        .clientHeight ||
      0;

    footerLocalizationVisible =
      rect.bottom > 0 &&
      rect.top < viewportHeight;

    if (
      !footerLocalizationVisible
    ) {
      triggerWrap.style.removeProperty(
        '--sct-rec-trigger-bottom'
      );

      return;
    }

    const gapPx = 12;

    const liftedBottom =
      Math.max(
        16,
        viewportHeight -
          rect.top +
          gapPx
      );

    triggerWrap.style.setProperty(
      '--sct-rec-trigger-bottom',
      liftedBottom + 'px'
    );
  }

  function scheduleTriggerPositionUpdate() {
    if (triggerPositionFrame) {
      return;
    }

    triggerPositionFrame =
      requestAnimationFrame(
        () => {
          triggerPositionFrame = null;
          updateTriggerPositionForFooter();
        }
      );
  }

  function watchFooterLocalization() {
    const localization =
      document.querySelector(
        '.footer__localization'
      );

    if (!localization) {
      return;
    }

    if (
      'IntersectionObserver' in window
    ) {
      footerLocalizationObserver =
        new IntersectionObserver(
          () => {
            scheduleTriggerPositionUpdate();
          },
          {
            root: null,
            threshold: [
              0,
              0.01,
              0.25,
              0.5,
              1,
            ],
          }
        );

      footerLocalizationObserver.observe(
        localization
      );
    }

    window.addEventListener(
      'scroll',
      scheduleTriggerPositionUpdate,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      scheduleTriggerPositionUpdate,
      { passive: true }
    );

    scheduleTriggerPositionUpdate();
  }

  function showTrigger() {
    const triggerWrap =
      getTriggerWrap();

    if (
      triggerWrap &&
      eligible() &&
      !sheetIsOpen()
    ) {
      triggerWrap.hidden = false;
      scheduleTriggerPositionUpdate();
    }
  }

  function hideTrigger() {
    const triggerWrap =
      getTriggerWrap();

    if (triggerWrap) {
      triggerWrap.hidden = true;
    }
  }

  function openSheet(
    source = 'manual'
  ) {
    const sheet = getSheet();
    const triggerButton =
      getTriggerButton();

    if (!sheet) return false;

    sheet.hidden = false;
    sheet.setAttribute(
      'aria-hidden',
      'false'
    );

    requestAnimationFrame(
      () => {
        sheet.classList.add(
          'is-open'
        );
      }
    );

    if (triggerButton) {
      triggerButton.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    hideTrigger();
    markSurfaceSeen();

    window.SctAnalytics
      ?.track?.(
        sheet,
        {
          action_type:
            'recommendation_open',
          item_type:
            'recommendation',
          tab_name:
            source,
        }
      );

    return true;
  }

  function closeSheet(
    rememberDismissal = true
  ) {
    const sheet = getSheet();
    const triggerButton =
      getTriggerButton();

    if (!sheet) return;

    sheet.classList.remove(
      'is-open'
    );

    sheet.setAttribute(
      'aria-hidden',
      'true'
    );

    if (triggerButton) {
      triggerButton.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    if (rememberDismissal) {
      markDismissed();
    }

    window.setTimeout(
      () => {
        sheet.hidden = true;
      },
      350
    );

    showTrigger();
  }

  async function loadAndOpen(
    source
  ) {
    if (!eligible()) return;

    const loadedProducts =
      await getProducts();

    if (
      loadedProducts.length <
      CONFIG.minDisplayProducts
    ) {
      return;
    }

    if (!renderProducts()) {
      return;
    }

    openSheet(source);
  }

  function bindEvents() {
    getTriggerButton()
      ?.addEventListener(
        'click',
        () => {
          loadAndOpen(
            'manual'
          );
        }
      );

    document.querySelector(
      '[data-sct-rec-bottom-close]'
    )?.addEventListener(
      'click',
      () => {
        closeSheet(true);
      }
    );

    document.addEventListener(
      'keydown',
      event => {
        if (
          event.key !== 'Escape'
        ) {
          return;
        }

        const sheet =
          getSheet();

        if (
          !sheet ||
          sheet.hidden ||
          !sheet.classList.contains(
            'is-open'
          )
        ) {
          return;
        }

        closeSheet(true);
      }
    );
  }

  function maybeAutoOpen() {
    if (
      !eligible() ||
      autoShownThisSession() ||
      surfaceSeenThisSession() ||
      autoDismissed()
    ) {
      return;
    }

    window.setTimeout(
      async () => {
        if (
          !eligible() ||
          autoShownThisSession() ||
          surfaceSeenThisSession() ||
          autoDismissed()
        ) {
          return;
        }

        const loadedProducts =
          await getProducts();

        if (
          loadedProducts.length <
          CONFIG.minDisplayProducts
        ) {
          return;
        }

        if (!renderProducts()) {
          return;
        }

        markAutoShown();
        openSheet('auto');
      },
      CONFIG.autoDelayMs
    );
  }

  function isTreatmentGroup() {
    return (
      window
        .SctRecommendationExperiment
        ?.group ===
      'treatment'
    );
  }

  function init() {
    if (!isTreatmentGroup()) {
      if (debug) {
        console.log(
          '[SCT Recommendation Bottom Sheet] Control group: UI suppressed.'
        );
      }

      return;
    }

    if (!eligible()) {
      return;
    }

    bindEvents();
    watchFooterLocalization();
    showTrigger();
    maybeAutoOpen();

    if (debug) {
      console.log(
        '[SCT Recommendation Bottom Sheet] Ready.',
        {
          viewed:
            profile()
              ?.uniqueViewedProducts,
          autoShown:
            autoShownThisSession(),
          dismissed:
            autoDismissed(),
        }
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();